import React from 'react';
import { ShieldCheckIcon, MailIcon, MapPinIcon } from 'lucide-react';
const sections = [{
  id: 'info-we-collect',
  label: '1. What Information Do We Collect?'
}, {
  id: 'how-we-process',
  label: '2. How Do We Process Your Information?'
}, {
  id: 'sms',
  label: '3. SMS / Text Message Communications'
}, {
  id: 'sharing',
  label: '4. When And With Whom Do We Share Your Personal Information?'
}, {
  id: 'cookies',
  label: '5. Do We Use Cookies And Other Tracking Technologies?'
}, {
  id: 'retention',
  label: '6. How Long Do We Keep Your Information?'
}, {
  id: 'security',
  label: '7. How Do We Keep Your Information Safe?'
}, {
  id: 'minors',
  label: '8. Do We Collect Information From Minors?'
}, {
  id: 'rights',
  label: '9. What Are Your Privacy Rights?'
}, {
  id: 'dnt',
  label: '10. Controls For Do-Not-Track Features'
}, {
  id: 'updates',
  label: '11. Do We Make Updates To This Notice?'
}, {
  id: 'contact',
  label: '12. How Can You Contact Us About This Notice?'
}];
export function PrivacyPolicy() {
  const lastUpdated = 'May 20, 2026';
  return <div className="min-h-screen bg-white" data-id="element-2261">
      {/* Header */}
      <header className="bg-slate-50 border-b border-slate-200" data-id="element-2262">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-2263">
          <div className="flex items-center gap-3 mb-4" data-id="element-2264">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-2265">
              <ShieldCheckIcon className="w-5 h-5 text-amber-600" data-id="element-2266" />
            </div>
            <span className="text-sm font-semibold text-amber-600 uppercase tracking-wider" data-id="element-2267">
              Legal
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3 tracking-tight" data-id="element-2268">
            Privacy Policy
          </h1>
          <p className="text-slate-600" data-id="element-2269">
            Last updated:{' '}
            <span className="font-medium text-slate-700" data-id="element-2270">{lastUpdated}</span>
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-2271">
        {/* Intro */}
        <section className="prose prose-slate max-w-none" data-id="element-2272">
          <p className="text-lg text-slate-700 leading-relaxed" data-id="element-2273">
            This privacy notice for <strong data-id="element-2274">Findservicequotes.io</strong>{' '}
            describes how and why we might collect, store, use, and/or share
            your information when you use our services, such as when you:
          </p>
          <ul className="text-slate-700 space-y-2 my-4 list-disc list-inside" data-id="element-2275">
            <li data-id="element-2276">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2277">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p className="text-slate-700 leading-relaxed" data-id="element-2278">
            <strong data-id="element-2279">Questions or concerns?</strong> Reading this privacy notice
            will help you understand your privacy rights and choices. If you do
            not agree with our policies and practices, please do not use our
            services.
          </p>
        </section>

        {/* Summary of key points */}
        <section className="mt-12 bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8" data-id="element-2280">
          <h2 className="text-xl font-bold text-slate-900 mb-6 uppercase tracking-wider" data-id="element-2281">
            Summary of Key Points
          </h2>
          <dl className="space-y-5" data-id="element-2282">
            {[{
            q: 'What personal information do we process?',
            a: 'When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use.'
          }, {
            q: 'Do we process sensitive personal information?',
            a: 'We do not process sensitive personal information.'
          }, {
            q: 'Do we receive information from third parties?',
            a: 'We may receive technical and advertising data from third parties, including advertising platforms and analytics providers.'
          }, {
            q: 'How do we process your information?',
            a: 'To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required.'
          }, {
            q: 'When and with whom do we share personal information?',
            a: 'Only in specific circumstances (e.g., business transfers or service providers).'
          }, {
            q: 'How do we keep your information safe?',
            a: 'With organizational and technical measures, but no method is 100% secure.'
          }, {
            q: 'What are your rights?',
            a: 'Depending on your location, you may have rights under applicable privacy laws.'
          }, {
            q: 'How do you exercise your rights?',
            a: 'You can exercise rights by contacting us (see Section 12).'
          }].map(item => <div key={item.q} data-id="element-2283">
                <dt className="font-semibold text-slate-900" data-id="element-2284">{item.q}</dt>
                <dd className="text-slate-700 mt-1 leading-relaxed" data-id="element-2285">
                  {item.a}
                </dd>
              </div>)}
          </dl>
        </section>

        {/* Table of contents */}
        <section className="mt-12" data-id="element-2286">
          <h2 className="text-xl font-bold text-slate-900 mb-4 uppercase tracking-wider" data-id="element-2287">
            Table of Contents
          </h2>
          <ol className="space-y-2 border-l-2 border-slate-200 pl-5" data-id="element-2288">
            {sections.map(s => <li key={s.id} data-id="element-2289">
                <a href={`#${s.id}`} className="text-slate-700 hover:text-amber-600 hover:underline transition-colors" data-id="element-2290">
                  {s.label}
                </a>
              </li>)}
          </ol>
        </section>

        {/* Section 1 */}
        <section id="info-we-collect" className="mt-14 scroll-mt-24" data-id="element-2291">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2292">
            1. What Information Do We Collect?
          </h2>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2293">
            Personal information you disclose to us
          </h3>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-2294">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </p>
          <p className="text-slate-700 leading-relaxed mb-3" data-id="element-2295">
            <strong data-id="element-2296">Personal Information Provided by You.</strong> The personal
            information we collect may include:
          </p>
          <ul className="text-slate-700 space-y-1 mb-6 list-disc list-inside" data-id="element-2297">
            <li data-id="element-2298">Names</li>
            <li data-id="element-2299">Phone numbers</li>
            <li data-id="element-2300">Email addresses</li>
            <li data-id="element-2301">Mailing addresses</li>
            <li data-id="element-2302">Education level and interests</li>
            <li data-id="element-2303">Program preferences</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2304">
            Information automatically collected
          </h3>
          <p className="text-slate-700 leading-relaxed mb-3" data-id="element-2305">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </p>
          <ul className="text-slate-700 space-y-1 list-disc list-inside" data-id="element-2306">
            <li data-id="element-2307">IP address</li>
            <li data-id="element-2308">Browser and device characteristics</li>
            <li data-id="element-2309">Operating system</li>
            <li data-id="element-2310">Language preferences</li>
            <li data-id="element-2311">Referring URLs</li>
            <li data-id="element-2312">Device name and type</li>
            <li data-id="element-2313">Pages viewed and time spent</li>
            <li data-id="element-2314">Clickstream data</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section id="how-we-process" className="mt-14 scroll-mt-24" data-id="element-2315">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2316">
            2. How Do We Process Your Information?
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-2317">
            We process your personal information for a variety of reasons,
            including:
          </p>
          <ul className="text-slate-700 space-y-2 mb-6 list-disc list-inside" data-id="element-2318">
            <li data-id="element-2319">To facilitate account creation and authentication</li>
            <li data-id="element-2320">To deliver and facilitate delivery of services to you</li>
            <li data-id="element-2321">To respond to user inquiries and offer support</li>
            <li data-id="element-2322">To send administrative information to you</li>
            <li data-id="element-2323">To fulfill and manage your requests</li>
            <li data-id="element-2324">To enable user-to-user communications</li>
            <li data-id="element-2325">
              To request feedback and contact you about your use of our services
            </li>
            <li data-id="element-2326">To send you marketing and promotional communications</li>
            <li data-id="element-2327">To deliver targeted advertising to you</li>
            <li data-id="element-2328">
              To protect our services and investigate potential violations
            </li>
            <li data-id="element-2329">
              To identify usage trends and determine the effectiveness of
              campaigns
            </li>
            <li data-id="element-2330">To save or protect an individual&apos;s vital interest</li>
            <li data-id="element-2331">To comply with our legal obligations</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2332">
            Email Communications
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2333">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from findservicequotes.io (operated
            by Media Matchers LLC) and its educational partners at the email
            address you provide. Marketing emails are sent from{' '}
            <a href="mailto:admin@media-matchers.com" className="text-amber-600 hover:underline" data-id="element-2334">
              admin@media-matchers.com
            </a>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </p>
        </section>

        {/* Section 3 */}
        <section id="sms" className="mt-14 scroll-mt-24" data-id="element-2335">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2336">
            3. SMS / Text Message Communications
          </h2>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2337">
            How We Collect SMS Consent
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2338">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from findservicequotes.io (operated by Media Matchers LLC)
            and its educational partners at the mobile phone number you provide.
            This consent is obtained at the point of form submission and is
            documented with the date, time, IP address, and page URL at which
            consent was given.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2339">
            How We Use Your Phone Number for SMS
          </h3>
          <p className="text-slate-700 leading-relaxed mb-3" data-id="element-2340">
            If you have provided consent, we may send you text messages for
            purposes including:
          </p>
          <ul className="text-slate-700 space-y-1 list-disc list-inside" data-id="element-2341">
            <li data-id="element-2342">
              Information about educational programs matching your interests
            </li>
            <li data-id="element-2343">Follow-up communications related to your inquiry</li>
            <li data-id="element-2344">
              Promotional offers and marketing messages from us or our
              educational partners
            </li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2345">
            Consent Is Not Required
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2346">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2347">
            Message Frequency and Rates
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2348">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2349">
            How to Opt Out
          </h3>
          <p className="text-slate-700 leading-relaxed mb-3" data-id="element-2350">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </p>
          <ul className="text-slate-700 space-y-1 mb-3 list-disc list-inside" data-id="element-2351">
            <li data-id="element-2352">
              Replying <strong data-id="element-2353">STOP</strong> to any text message you receive
              from us
            </li>
            <li data-id="element-2354">Contacting us directly using the information in Section 12</li>
          </ul>
          <p className="text-slate-700 leading-relaxed" data-id="element-2355">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2356">
            For Assistance
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2357">
            Reply <strong data-id="element-2358">HELP</strong> to any text message for support
            information, or contact us directly using the information in Section
            12.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2359">
            Third-Party SMS Senders
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2360">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-2361">
            Record Retention
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2362">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </p>
        </section>

        {/* Section 4 */}
        <section id="sharing" className="mt-14 scroll-mt-24" data-id="element-2363">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2364">
            4. When And With Whom Do We Share Your Personal Information?
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-2365">
            We may share information in the following situations:
          </p>
          <ul className="text-slate-700 space-y-3 list-disc list-inside" data-id="element-2366">
            <li data-id="element-2367">
              <strong data-id="element-2368">Business Transfers:</strong> In connection with, or during
              negotiations of, any merger, sale of company assets, financing, or
              acquisition.
            </li>
            <li data-id="element-2369">
              <strong data-id="element-2370">Educational Institutions:</strong> We share your
              information with schools and educational partners that match your
              interests.
            </li>
            <li data-id="element-2371">
              <strong data-id="element-2372">Service Providers:</strong> With third-party vendors,
              service providers, contractors, or agents who perform services for
              us.
            </li>
            <li data-id="element-2373">
              <strong data-id="element-2374">Advertising Partners:</strong> With advertising partners
              to deliver targeted ads.
            </li>
            <li data-id="element-2375">
              <strong data-id="element-2376">Legal Requirements:</strong> When required by law or to
              protect rights, safety, and property.
            </li>
          </ul>
        </section>

        {/* Section 5 */}
        <section id="cookies" className="mt-14 scroll-mt-24" data-id="element-2377">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2378">
            5. Do We Use Cookies And Other Tracking Technologies?
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2379">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </p>
        </section>

        {/* Section 6 */}
        <section id="retention" className="mt-14 scroll-mt-24" data-id="element-2380">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2381">
            6. How Long Do We Keep Your Information?
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2382">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </p>
        </section>

        {/* Section 7 */}
        <section id="security" className="mt-14 scroll-mt-24" data-id="element-2383">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2384">
            7. How Do We Keep Your Information Safe?
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2385">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </p>
        </section>

        {/* Section 8 */}
        <section id="minors" className="mt-14 scroll-mt-24" data-id="element-2386">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2387">
            8. Do We Collect Information From Minors?
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2388">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </p>
        </section>

        {/* Section 9 */}
        <section id="rights" className="mt-14 scroll-mt-24" data-id="element-2389">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2390">
            9. What Are Your Privacy Rights?
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-2391">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="text-slate-700 space-y-2 mb-6 list-disc list-inside" data-id="element-2392">
            <li data-id="element-2393">The right to access your personal information</li>
            <li data-id="element-2394">
              The right to request correction of your personal information
            </li>
            <li data-id="element-2395">The right to request deletion of your personal information</li>
            <li data-id="element-2396">The right to opt out of marketing communications</li>
            <li data-id="element-2397">
              The right to opt out of the sale or sharing of personal
              information (where applicable)
            </li>
          </ul>
          <p className="text-slate-700 leading-relaxed" data-id="element-2398">
            To exercise these rights, please contact us using the information in
            Section 12.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3" data-id="element-2399">
            Opt Out of Marketing Email
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2400">
            To opt out of marketing email: Click the unsubscribe link in any
            marketing email you receive from us, or contact us at the address in
            Section 12. Requests are processed within 10 business days.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3" data-id="element-2401">
            California Residents — Opt Out of Sale/Sharing
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-2402">
            California residents — opt out of sale/sharing of personal
            information: To exercise your right to opt out under CCPA/CPRA,
            contact us at the address in Section 12 with the subject line{' '}
            <strong data-id="element-2403">&ldquo;California Privacy Opt-Out.&rdquo;</strong> We will
            process your request within 15 business days.
          </p>
        </section>

        {/* Section 10 */}
        <section id="dnt" className="mt-14 scroll-mt-24" data-id="element-2404">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2405">
            10. Controls For Do-Not-Track Features
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2406">
            Most web browsers and some mobile operating systems include a
            Do-Not-Track (&ldquo;DNT&rdquo;) feature or setting you can activate
            to signal your privacy preference. At this stage, no uniform
            technology standard for recognizing and implementing DNT signals has
            been finalized.
          </p>
        </section>

        {/* Section 11 */}
        <section id="updates" className="mt-14 scroll-mt-24" data-id="element-2407">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2408">
            11. Do We Make Updates To This Notice?
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2409">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            &ldquo;Last updated&rdquo; date at the top of this privacy notice.
          </p>
        </section>

        {/* Section 12 */}
        <section id="contact" className="mt-14 scroll-mt-24" data-id="element-2410">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-2411">
            12. How Can You Contact Us About This Notice?
          </h2>
          <p className="text-slate-700 leading-relaxed mb-6" data-id="element-2412">
            If you have questions or comments about this notice, please contact
            us at:
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8" data-id="element-2413">
            <p className="font-bold text-slate-900 text-lg mb-4" data-id="element-2414">
              Media Matchers LLC
            </p>
            <div className="space-y-3" data-id="element-2415">
              <div className="flex items-start gap-3" data-id="element-2416">
                <MapPinIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-2417" />
                <p className="text-slate-700" data-id="element-2418">
                  1865 Chestnut Street
                  <br data-id="element-2419" />
                  San Francisco, CA 94123
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-2420">
                <MailIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-2421" />
                <a href="mailto:admin@media-matchers.com" className="text-amber-600 hover:underline font-medium" data-id="element-2422">
                  admin@media-matchers.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}